/**
 * Wave 29 Chunk 8 — /team/leave/new full-page leave applicaton.
 *
 * Replaces the banned `ApplyModal` from LeaveManager.jsx
 * (DESIGN_CONTRACT Rule 0 — no small modal views).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarDays, ChevronLeft, Loader2, Send,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import HolidayWarning from "../components/HolidayWarning";
import DatePickerInput from "../components/DatePickerInput";
import VoiceTextarea from "../components/VoiceTextarea";

const KINDS = [
  { v: "annual", l: "Annual" },
  { v: "sick", l: "Sick" },
  { v: "family", l: "Family responsibility" },
  { v: "unpaid", l: "Unpaid" },
  { v: "study", l: "Study" },
  { v: "maternity", l: "Maternity" },
];

export default function LeaveApply() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [kind, setKind] = useState("annual");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (!user || user.role === "client") return;
    (async () => {
      try {
        const { data } = await api.get("/team/leave/balance/me");
        setBalance(data);
      } catch (_e) { /* silent */ }
    })();
  }, [user]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!startDate || !endDate) return toast.error("Pick start and end dates.");
    if (endDate < startDate) return toast.error("End date can't be before start.");
    setBusy(true);
    try {
      await api.post("/team/leave/applications", {
        kind,
        start_date: startDate,
        end_date: endDate,
        reason: reason.trim() || null,
      });
      toast.success("Leave applied. HR has been notified.");
      nav("/team/leave");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Apply failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="leave-apply-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/team/leave" className="hv-btn hv-btn-ghost text-xs" data-testid="leave-apply-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Leave
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#1FB6C1]" /> Apply for leave
        </h1>
        <span />
      </div>

      {balance && (
        <div className="hv-card p-3 flex items-center gap-4 text-xs" data-testid="leave-apply-balance">
          <div>
            <span className="text-[#647087]">Allowance:</span>{" "}
            <b className="text-white">{balance.annual_allowance}d</b>
          </div>
          <div>
            <span className="text-[#647087]">Used (12m):</span>{" "}
            <b className="text-amber-300">{balance.annual_used_12m}d</b>
          </div>
          <div>
            <span className="text-[#647087]">Remaining:</span>{" "}
            <b className="text-emerald-300">{balance.annual_remaining}d</b>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="hv-card p-4 sm:p-5 space-y-4" data-testid="leave-apply-form">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Kind of leave</div>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="hv-input text-sm w-full"
            data-testid="apply-kind"
          >
            {KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
          </select>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Start date</div>
            <DatePickerInput
              value={startDate}
              onChange={setStartDate}
              testId="apply-start"
            />
            <HolidayWarning dateIso={startDate} label="Start" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">End date</div>
            <DatePickerInput
              value={endDate}
              onChange={setEndDate}
              testId="apply-end"
            />
            <HolidayWarning dateIso={endDate} label="End" />
          </label>
        </div>

        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Reason (optional)</div>
          <VoiceTextarea
            value={reason}
            onChange={setReason}
            rows={4}
            data-testid="apply-reason"
            placeholder="e.g. Family trip to the coast"
           testIdPrefix="apply-reason-voice" context="note" />
        </label>

        <p className="text-[11px] text-[#a0aabf]">
          Working-day math automatically excludes weekends and South African public holidays.
          HR will be notified when you submit.
        </p>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
          <Link to="/team/leave" className="hv-btn hv-btn-ghost text-xs" data-testid="apply-cancel">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="apply-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Submit application
          </button>
        </div>
      </form>
    </div>
  );
}
